import {
  Alert,
  BodyLarge,
  CallingCodeInput,
  Severity,
  createEventData,
  createPhoneNumberWithCallingCode,
  isEmpty,
  preparedValidationSchemaForContacts,
  pushToDataLayer,
  theme,
  useToast,
} from '@arnold/common';
import styled from '@emotion/styled';
import { Form, Formik, FormikValues } from 'formik';
import { equals } from 'ramda';
import { useState } from 'react';
import { Button, Col, FormControl, FormGroup, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  ContactInput,
  ContactSource,
  ContactType,
  SurveyGroupRespondentsQuery,
  UpdateRespondentInput,
  UpdateRespondentMutation,
  useOrganizationSettingQuery,
  useUpdateRespondentMutation,
  useVerifyPhoneNumbersMutation,
} from '../../generated/hooks';
import { FormErrorFeedback, FormGroupLabel, FormRadioInput } from '../Common';
import { InfoMessage } from '../Forms/RespondentForm/RespondentForm';
import { handlePhoneBlur, handlePhoneChange } from '../Forms/RespondentForm/utils';
import Loading from '../Loading';
import BaseModal from './BaseModal';

type Member = SurveyGroupRespondentsQuery['surveyGroupRespondents']['data'][0]['respondent'];
type Props = {
  cancel: () => void;
  respondent: Member;
  contact?: string;
  organizationId: string;
  userId: string;
  isEmployeeManagementDisabled?: boolean;
};
const RoundedContent = styled.div`
  border: 1px solid ${theme.colors.borderMain.default};
  padding: ${theme.spacing.h};
  padding-bottom: 0px;
  border-radius: ${theme.borderRadius.l};
  margin-top: 40px;
`;

const RadioButtonError = styled.div`
  display: flex;
  width: 100%;
  margin-top: -1.5rem;

  & .invalid-feedback {
    display: block;
  }
`;

export function UpdateRespondent({
  cancel,
  respondent,
  organizationId,
  contact,
  userId,
  isEmployeeManagementDisabled,
}: Props) {
  const { t } = useTranslation('updateRespondent');
  const { addToast } = useToast();
  const [contactInfo, setContactInfo] = useState(true);

  const { data: organizationSettings, loading: organizationSettingsLoading } = useOrganizationSettingQuery({
    variables: { organizationId },
  });

  const handleMutationCompleted = (data: UpdateRespondentMutation) => {
    addToast(
      t('updateContactSuccess', {
        respondentName: `${data.updateRespondent.firstname} ${data.updateRespondent.surname}`,
      }),
    );

    pushToDataLayer({
      userId,
      orgId: organizationId,
      event: 'ux.respondent-update-success',
      ...createEventData('organization', 'updateRespondentSuccess', 'organization update respondent success'),
    });
  };

  const handleMutationError = () => {
    pushToDataLayer({
      userId,
      orgId: organizationId,
      event: 'ux.respondent-update-error',
      ...createEventData('organization', 'updateRespondentError', 'organization update respondent error'),
    });
  };
  const [updateRespondentMutation] = useUpdateRespondentMutation({
    onCompleted: handleMutationCompleted,
    onError: handleMutationError,
  });

  const [unverifiedPhoneNumber, setUnverifiedPhoneNumber] = useState(false);
  const [verifyPhoneNumbers] = useVerifyPhoneNumbersMutation();
  const workEmail =
    respondent.contacts?.find((c) => c.source === ContactSource.Primary && c.type === ContactType.Email)?.value || '';
  const personalEmail =
    respondent.contacts?.find((c) => c.source === ContactSource.Respondent && c.type === ContactType.Email)?.value ||
    '';
  const phone = respondent.contacts?.find((c) => c.type === ContactType.Sms)?.phoneNumber?.nationalNumber || '';
  const countryCallingCode =
    respondent.contacts?.find((c) => c.type === ContactType.Sms)?.phoneNumber?.countryCallingCode ?? '';
  const RespondentFormValidationSchema = Yup.object().shape(preparedValidationSchemaForContacts(t));

  if (organizationSettingsLoading || !organizationSettings) {
    return <Loading />;
  }

  const getContacts = (values: FormikValues) => {
    const contacts: ContactInput[] = [];
    if (workEmail !== values.primaryEmail) {
      contacts.push({
        source: ContactSource.Primary,
        contactType: ContactType.Email,
        value: values.primaryEmail,
      });
    }
    if ((personalEmail || values.secondaryEmail) && personalEmail !== values.secondaryEmail) {
      contacts.push({
        source: ContactSource.Respondent,
        contactType: ContactType.Email,
        value: values.secondaryEmail || null,
      });
    }
    if ((phone || values.phone) && (phone !== values.phone || countryCallingCode !== values.callingCode)) {
      contacts.push({
        source: ContactSource.Respondent,
        contactType: ContactType.Sms,
        value: values.phone ? createPhoneNumberWithCallingCode(values.phone, values.callingCode) : null,
      });
    }

    return contacts;
  };
  const createUpdateInput = (values: FormikValues) => {
    const updateInput: UpdateRespondentInput = {};

    Object.keys(values).forEach((key) => {
      if (
        (respondent[key as keyof Member] || respondent[key as keyof Member] === '' || key === 'disabled') &&
        respondent[key as keyof Member] !== values[key]
      ) {
        updateInput[key as keyof UpdateRespondentInput] = values[key];
      }
    });

    const contacts = getContacts(values);

    if (contacts.length > 0) {
      updateInput.contacts = contacts;
    }

    return updateInput;
  };

  const initialValues = {
    primaryEmail: workEmail,
    secondaryEmail: personalEmail,
    phone,
    callingCode: countryCallingCode || organizationSettings.organization?.config?.countryCallingCode || '',
    preferredContactType: (respondent && respondent.preferredContactType) || ContactType.Email,
  };
  return (
    <BaseModal
      width={600}
      onClose={cancel}
      title={t('updateRespondentModal:title')}
      description={
        <div>
          {contactInfo && (
            <Alert severity={Severity.DANGER} onClose={() => setContactInfo(false)}>
              <Trans
                i18nKey={'updateRespondentModal:contactInfo'}
                values={{
                  contact,
                }}
                components={[<strong />]}
              />
            </Alert>
          )}
          {isEmployeeManagementDisabled && <InfoMessage>{t('employeesUpdateInfoDescription')}</InfoMessage>}

          <Formik
            initialValues={initialValues}
            validationSchema={RespondentFormValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSubmitting(true);
                const input = createUpdateInput(values);
                if (Object.keys(input).length !== 0) {
                  await updateRespondentMutation({
                    variables: {
                      id: respondent.id,
                      input,
                    },
                  });
                }
              } catch (e) {
                setSubmitting(false);
              }
              setSubmitting(false);
              cancel();
            }}
          >
            {({
              values,
              errors,
              touched,
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              isSubmitting,
              setValues,
            }) => (
              <Form onSubmit={handleSubmit} data-icom={'form-respondent'}>
                <RoundedContent>
                  <BodyLarge bold margin={`0px 0px ${theme.spacing.g}`}>
                    {respondent.firstname} {respondent.surname}
                  </BodyLarge>
                  <Row>
                    <FormGroup as={Col}>
                      <FormGroupLabel>{t('teamsScreen:workEmail')} (Arnold ID)</FormGroupLabel>
                      <FormControl
                        type="text"
                        name="primaryEmail"
                        value={values.primaryEmail || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.primaryEmail && !!errors.primaryEmail}
                        disabled={isEmployeeManagementDisabled}
                      />
                      <FormErrorFeedback error={errors.primaryEmail} />
                    </FormGroup>
                  </Row>
                  <Row>
                    <FormGroup as={Col}>
                      <FormGroupLabel>{t('teamsScreen:personalEmail')}</FormGroupLabel>
                      <FormControl
                        type="email"
                        name="secondaryEmail"
                        value={values.secondaryEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!errors.secondaryEmail}
                      />
                      <FormErrorFeedback error={errors.secondaryEmail} />
                    </FormGroup>
                  </Row>
                  <Row>
                    <FormGroup as={Col} sm={6}>
                      <FormGroupLabel>{t('updateRespondent:callingCode')}</FormGroupLabel>
                      <CallingCodeInput
                        callingCode={values.callingCode || ''}
                        onChange={(callingCode: string) => {
                          setValues(() => ({ ...values, callingCode }));
                        }}
                        isInvalid={!!errors.callingCode}
                      />
                      <FormErrorFeedback error={errors.callingCode} />
                    </FormGroup>
                    <FormGroup as={Col} sm={6}>
                      <FormGroupLabel>{t('teamsScreen:phone')}</FormGroupLabel>
                      <FormControl
                        type="tel"
                        name="phone"
                        value={values.phone}
                        onChange={handlePhoneChange(handleChange)}
                        onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handlePhoneBlur(values.callingCode, e.target.value, (internationalPhoneNumber: string) =>
                            verifyPhoneNumbers({
                              variables: { phoneNumbers: [internationalPhoneNumber] },
                              onCompleted: (data) => {
                                setUnverifiedPhoneNumber(
                                  !data.verifyPhoneNumbers.find((pn) => pn.phoneNumber === internationalPhoneNumber),
                                );
                              },
                              onError: () => {
                                setUnverifiedPhoneNumber(false);
                              },
                            }),
                          )
                        }
                        isInvalid={!!errors.phone || unverifiedPhoneNumber}
                      />
                      <FormErrorFeedback error={errors.phone || (unverifiedPhoneNumber && t('enterValidPhone'))} />
                    </FormGroup>
                  </Row>
                  <Row>
                    <FormGroup as={Col}>
                      <FormGroupLabel>{t('sendSurveyTo')}</FormGroupLabel>
                      <FormGroup className="form-check-group">
                        <FormRadioInput
                          name="preferredContactType"
                          value={ContactType.Email}
                          clicked={() => {
                            setFieldValue('preferredContactType', ContactType.Email);
                          }}
                          changed={handleChange}
                          onBlur={handleBlur}
                          defaultChecked={values.preferredContactType === ContactType.Email}
                          label={t('teamsScreen:email')}
                        />
                        <FormRadioInput
                          name="preferredContactType"
                          value={ContactType.Sms}
                          clicked={() => {
                            setFieldValue('preferredContactType', ContactType.Sms);
                          }}
                          changed={handleChange}
                          onBlur={handleBlur}
                          defaultChecked={values.preferredContactType === ContactType.Sms}
                          label={t('teamsScreen:phone')}
                          disabled={!values.phone || errors.phone || unverifiedPhoneNumber}
                        />
                      </FormGroup>
                      <RadioButtonError>
                        <FormErrorFeedback error={errors.preferredContactType} />
                      </RadioButtonError>
                    </FormGroup>
                  </Row>
                </RoundedContent>
                <div className="d-flex flex-wrap align-items-start mt-7">
                  <Button
                    variant="primary"
                    className={'mr-5 mb-5'}
                    disabled={
                      (!isEmpty(errors) && Object.keys(errors).length !== 0) ||
                      isSubmitting ||
                      unverifiedPhoneNumber ||
                      equals(values, initialValues)
                    }
                    type="submit"
                  >
                    {t('weekPicker:save')}
                  </Button>
                  <Button
                    color="primary"
                    variant="outline-primary"
                    className={'mb-5'}
                    onClick={() => {
                      cancel();
                    }}
                    disabled={isSubmitting}
                    type="button"
                  >
                    {t('common:close')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
    />
  );
}
